import type { ProgressStep, ProgressNode, InContextEntry, SupersededEntry, TokenAttribution } from '@localagent/sdk';
import type { CognitiveToolType } from '../../types/reasoning-types';
import type { ExecutionTreeNode, ExecutionTreeNodeStatus } from '@localagent/ui';
import { isContextTool } from './ReasoningProgress';
import { formatPersonaName } from '../../utils/persona-display';

export interface LlmCallMetadata {
  type: 'llm_call';
  id?: string;
  iteration?: number;
  duration_ms?: number;
  tokens?: number;
  prompt_tokens?: number;
  completion_tokens?: number;
  tokens_per_second?: number;
  model_name?: string;
  thinking_preview?: string;
  response_preview?: string;
  tool_names?: string[];
  has_tool_calls?: boolean;
}

export interface ContextUpdateMetadata {
  type: 'context_update';
  id?: string;
  iteration?: number;
  usage_percent?: number;
  total_tokens?: number;
  context_window_size?: number;
  prompt_tokens?: number;
  completion_tokens?: number;
  message_count?: number;
  persona_name?: string;
  attribution?: TokenAttribution;
  in_context_entries?: InContextEntry[];
  superseded_entries?: SupersededEntry[];
  compacted_entries?: InContextEntry[];
  stale_message_count?: number;
  tokens_saved_by_dedup?: number;
  overflow_handled?: boolean;
  messages_summarized?: number;
  tokens_reduced?: number;
}

export interface ContextRagMetadata {
  type: 'context_rag';
  id?: string;
  outcome?: 'hits' | 'empty' | 'failed';
  hit_count?: number;
  hit_refs?: Array<{ task_id: string; message_index: number }>;
  indexed_entries?: number;
}

export interface TriageMetadata {
  type: 'triage';
  id?: string;
  guidance_preview?: string;
}

export interface ToolCallMetadata {
  type: 'tool_call';
  id?: string;
  child_id?: string;
  tool_name?: string;
  tool_args_preview?: string;
  tool_result_preview?: string;
  tool_result_status?: string;
  duration_ms?: number;
}

export interface AgentExecutionMetadata {
  type: 'agent_execution';
  id: string;
  parent_id?: string;
  persona_name?: string;
  prompt_preview?: string;
  conclusion_summary?: string;
  artifacts?: string[];
  decisions?: string[];
  error?: string;
  cognitive_tool_type?: string;
  token_count?: number;
  duration_ms?: number;
  context_usage_percent?: number;
  context_has_compaction?: boolean;
}

export type ProgressNodeMetadata =
  | LlmCallMetadata
  | ContextUpdateMetadata
  | ContextRagMetadata
  | TriageMetadata
  | ToolCallMetadata
  | AgentExecutionMetadata;

export const COGNITIVE_TOOL_LABELS: Record<CognitiveToolType, string> = {
  explore: 'Explore',
  research: 'Research',
  plan: 'Plan',
  execute_plan: 'Execute',
  evaluate: 'Evaluate',
  audit: 'Audit',
  delegate: 'Delegate',
  triage: 'Triage',
};

export function isCognitiveTool(toolName: string | undefined): boolean {
  return toolName != null && toolName in COGNITIVE_TOOL_LABELS;
}

export function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60_000).toFixed(1)}m`;
}

export function formatTokens(tokens: number): string {
  if (tokens >= 1000) return `${(tokens / 1000).toFixed(1)}K`;
  return String(tokens);
}

function mapToolStatus(resultStatus: string | undefined): ExecutionTreeNodeStatus {
  if (!resultStatus) return 'pending';
  if (resultStatus === 'success') return 'completed';
  if (resultStatus === 'error') return 'failed';
  return 'completed';
}

export function stepToNode(step: ProgressStep, _showResults: boolean, idPrefix: string, index: number): ExecutionTreeNode {
  switch (step.type) {
    case 'llm_call': {
      const dur = step.durationMs ? ` (${formatDuration(step.durationMs)})` : '';
      const decision = step.toolNames && step.toolNames.length > 0 ? `→ ${step.toolNames.join(', ')}` : undefined;
      let subtitle = step.thinkingPreview;
      if (decision) {
        subtitle = subtitle ? `${subtitle}\n${decision}` : decision;
      } else if (!subtitle && step.responsePreview) {
        subtitle = step.responsePreview;
      }
      return {
        id: `${idPrefix}llm-${index}`,
        label: `thinking${dur}`,
        subtitle,
        contextTokens: step.tokens,
        status: 'completed',
        badge: step.hasToolCalls ? 'tool use' : 'text',
        metadata: {
          type: 'llm_call',
          iteration: step.iteration,
          duration_ms: step.durationMs,
          id: step.nodeId,
          tokens: step.tokens,
          prompt_tokens: step.promptTokens,
          completion_tokens: step.completionTokens,
          tokens_per_second: step.tokensPerSecond,
          model_name: step.modelName,
          thinking_preview: step.thinkingPreview,
          response_preview: step.responsePreview,
          tool_names: step.toolNames,
          has_tool_calls: step.hasToolCalls,
        } satisfies LlmCallMetadata,
      };
    }

    case 'tool_call': {
      const isCtx = isContextTool(step.toolName);
      const isCog = isCognitiveTool(step.toolName);
      // Row subtitle = args metadata ONLY.
      // The former 200-line `toolResultPreview` slice was removed: a
      // large result no longer rides the frame at all (it is skeletonized), and
      // the full result is fetched on demand via the expand → getEventData →
      // EventView path — never embedded here as a lossy truncation. The
      // `metadata` object below still carries tool_name/status/duration.
      const subtitle = step.toolArgsPreview;
      const dur = step.durationMs ? ` (${formatDuration(step.durationMs)})` : '';
      return {
        id: `${idPrefix}tool-${index}`,
        label: `${step.toolName ?? 'unknown tool'}${dur}`,
        subtitle,
        status: mapToolStatus(step.toolResultStatus),
        badge: isCtx ? 'context' : isCog ? 'cognitive' : undefined,
        metadata: {
          type: 'tool_call',
          tool_name: step.toolName,
          tool_args_preview: step.toolArgsPreview,
          tool_result_preview: step.toolResultPreview,
          tool_result_status: step.toolResultStatus,
          id: step.nodeId,
          child_id: step.childId,
          duration_ms: step.durationMs,
        } satisfies ToolCallMetadata,
      };
    }

    case 'triage':
      return {
        id: `${idPrefix}triage-${index}`,
        label: 'triage',
        subtitle: step.guidancePreview,
        status: 'completed',
        badge: 'Triage',
        metadata: { type: 'triage', id: step.nodeId, guidance_preview: step.guidancePreview } satisfies TriageMetadata,
      };

    case 'context_rag': {
      // Run-start retrieval step. Started events have no ragOutcome yet (in
      // progress); the completion's outcome picks the terminal state — hits
      // name the count, empty is the honest no-result state, failed is the
      // best-effort failure the run survived.
      const outcome = step.ragOutcome;
      const refs = step.hitRefs ?? [];
      let subtitle: string | undefined;
      if (outcome === 'hits') {
        subtitle = `${step.hitCount ?? refs.length} relevant earlier ${step.hitCount === 1 ? 'entry' : 'entries'} · corpus ${step.indexedEntries ?? '?'}`;
      } else if (outcome === 'empty') {
        subtitle =
          step.indexedEntries != null && step.indexedEntries > 0
            ? `no relevant context in ${step.indexedEntries} entries`
            : 'no relevant context found';
      } else if (outcome === 'failed') {
        subtitle = 'retrieval failed — continuing without it';
      }
      return {
        id: `${idPrefix}rag-${index}`,
        label: 'retrieving context',
        subtitle,
        status: !outcome ? 'running' : outcome === 'failed' ? 'failed' : 'completed',
        badge: 'context',
        metadata: {
          type: 'context_rag',
          id: step.nodeId,
          outcome,
          hit_count: step.hitCount,
          hit_refs: refs,
          indexed_entries: step.indexedEntries,
        } satisfies ContextRagMetadata,
      };
    }

    case 'context_update': {
      // Real percent + tokens label. usagePercent is undefined when the update carried no provider
      // usage (missing-usage rule) — then the step shows "context" with no
      // fabricated 0%. When the update deduped entries, name them in the subtitle.
      const pct = step.usagePercent;
      const tokens = step.totalContextTokens;
      const label =
        pct != null
          ? `context ${Math.round(pct)}%${tokens != null ? ` (${formatCtxTokens(tokens)})` : ''}`
          : 'context';
      const superseded = step.supersededEntries ?? [];
      const fragments: string[] = [];
      if (superseded.length > 0) {
        const names = superseded
          .map((e) => (e.source_args.path as string) ?? (e.source_args.url as string) ?? e.tool_name)
          .slice(0, 3);
        const reclaimed = superseded.reduce((acc, e) => acc + (e.estimated_tokens ?? 0), 0);
        fragments.push(`deduped ${names.join(', ')} · ${formatCtxTokens(reclaimed)} reclaimed`);
      }
      if (step.overflowHandled && step.messagesSummarized != null && step.messagesSummarized > 0) {
        const freed = step.tokensReduced != null && step.tokensReduced > 0 ? `, ${formatCtxTokens(step.tokensReduced)} freed` : '';
        fragments.push(`compacted ${step.messagesSummarized} msgs${freed}`);
      }
      const subtitle = fragments.length > 0 ? fragments.join(' · ') : undefined;
      return {
        id: `${idPrefix}ctx-${index}`,
        label,
        subtitle,
        status: 'completed',
        badge: step.overflowHandled ? 'compacted' : 'context',
        metadata: {
          type: 'context_update',
          id: step.nodeId,
          iteration: step.iteration,
          usage_percent: step.usagePercent,
          total_tokens: step.totalContextTokens,
          context_window_size: step.contextWindowSize,
          prompt_tokens: step.promptTokens,
          completion_tokens: step.completionTokens,
          message_count: step.messageCount,
          persona_name: step.personaName,
          attribution: step.attribution,
          in_context_entries: step.inContextEntries,
          superseded_entries: step.supersededEntries,
          compacted_entries: step.compactedEntries,
          stale_message_count: step.staleMessageCount,
          tokens_saved_by_dedup: step.tokensSavedByDedup,
          overflow_handled: step.overflowHandled,
          messages_summarized: step.messagesSummarized,
          tokens_reduced: step.tokensReduced,
        } satisfies ContextUpdateMetadata,
      };
    }
  }
}

function formatCtxTokens(tokens: number): string {
  if (tokens >= 1000) return `${(tokens / 1000).toFixed(1)}K`;
  return String(tokens);
}

function nodeStatusToTree(status: ProgressNode['status']): ExecutionTreeNodeStatus {
  switch (status) {
    case 'running':
      return 'running';
    case 'completed':
      return 'completed';
    case 'stopped':
      return 'stopped';
    case 'failed':
      return 'failed';
    default: {
      // Exhaustive: a new node status must be mapped deliberately, not silently
      // rendered as a red X the way the previous `return 'failed'` did.
      const unreachable: never = status;
      return unreachable;
    }
  }
}

export function buildChildNodes(
  steps: ProgressStep[],
  childNodes: ProgressNode[],
  showResults: boolean,
  idPrefix: string,
): ExecutionTreeNode[] {
  const nodesById = new Map(childNodes.map(n => [n.id, n]));
  const nestedIds = new Set<string>();
  const entries: Array<{ ts: string; node: ExecutionTreeNode }> = [];

  for (let i = 0; i < steps.length; i++) {
    const step = steps[i];
    const node = stepToNode(step, showResults, idPrefix, i);
    if (step.type === 'tool_call' && step.childIdAtStart) {
      const spawned = nodesById.get(step.childIdAtStart);
      if (spawned) {
        node.children = [nodeToTreeNode(spawned, showResults)];
        nestedIds.add(spawned.id);
      }
    }
    entries.push({ ts: step.timestamp, node });
  }
  for (const child of childNodes) {
    if (nestedIds.has(child.id)) continue;
    entries.push({ ts: child.startedAt, node: nodeToTreeNode(child, showResults) });
  }

  entries.sort((a, b) => a.ts.localeCompare(b.ts));
  return entries.map(e => e.node);
}

export function nodeToTreeNode(node: ProgressNode, showResults: boolean): ExecutionTreeNode {
  const allChildren = buildChildNodes(node.steps, node.children, showResults, `node-${node.id}-`);

  const ctxSteps = node.steps.filter((s) => s.type === 'context_update');
  const lastUsagePercent = [...ctxSteps].reverse().find((s) => s.usagePercent != null)?.usagePercent;
  const hasCompaction = ctxSteps.some((s) => s.overflowHandled === true);

  let subtitle = node.promptPreview;
  if (showResults && node.conclusionSummary) {
    subtitle = subtitle
      ? `${subtitle}\n--- conclusion ---\n${node.conclusionSummary}`
      : node.conclusionSummary;
  }

  const cogLabel = node.cognitiveToolType ? COGNITIVE_TOOL_LABELS[node.cognitiveToolType as CognitiveToolType] : undefined;
  const badge = cogLabel ?? formatPersonaName(node.personaName ?? '');

  const metrics: string[] = [];
  if ((node.tokenCount ?? 0) > 0) metrics.push(`${formatTokens(node.tokenCount ?? 0)} tokens`);
  if (node.durationMs) metrics.push(formatDuration(node.durationMs));
  const metricsStr = metrics.length > 0 ? ` (${metrics.join(', ')})` : '';

  return {
    id: `node-${node.id}`,
    label: `${node.label ?? formatPersonaName(node.personaName ?? '')}${metricsStr}`,
    badge,
    subtitle,
    contextTokens: (node.tokenCount ?? 0) > 0 ? node.tokenCount : undefined,
    status: nodeStatusToTree(node.status),
    children: allChildren.length > 0 ? allChildren : undefined,
    metadata: {
      type: 'agent_execution',
      id: node.id,
      parent_id: node.parentId,
      persona_name: node.personaName,
      prompt_preview: node.promptPreview,
      conclusion_summary: node.conclusionSummary,
      artifacts: node.artifacts,
      decisions: node.decisions,
      error: node.error,
      cognitive_tool_type: node.cognitiveToolType,
      token_count: node.tokenCount,
      duration_ms: node.durationMs,
      context_usage_percent: lastUsagePercent,
      context_has_compaction: hasCompaction,
    } satisfies AgentExecutionMetadata,
  };
}

export function stepsToTree(steps: ProgressStep[], nodes: ProgressNode[], showResults: boolean): ExecutionTreeNode[] {
  if (nodes.length === 0) {
    return steps.map((s, i) => stepToNode(s, showResults, '', i));
  }

  const byId = new Map(nodes.map(n => [n.id, n]));
  const rootNodes = nodes.filter(
    n => !n.parentId || !byId.has(n.parentId),
  );

  return buildChildNodes(steps, rootNodes, showResults, '');
}
